// src/components/PurchaseFlow/Steps/DataEntry/DataEntryStep.js
import React, { useState, useRef, useEffect } from 'react';
import api from '../../../../services/api';
import NavigationButtons from '../../NavigationButtons';
import PersonalInfo from './Sections/PersonalInfo';
import BankInformation from './Sections/BankInformation';
import AddressContact from './Sections/AddressContact';
import ContactInfo from './Sections/ContactInfo';
import Referrals from './Sections/Referrals';
import EmploymentInfo from './Sections/EmploymentInfo';
import Swal from 'sweetalert2';
import { useSuggestedData } from '../../../../context/SuggestedDataContext';
import './DataEntryContinuous.css';

const DataEntryStep = ({ onNext, onBack, formData: initialData = {}, clientData, purchaseRequestId }) => {
  const { suggestedData, setSuggestedData, selectedData, setSelectedData } = useSuggestedData();

  const [allowEditPersonalInfo] = useState(process.env.REACT_APP_ALLOW_EDIT_PERSONAL_INFO === 'true');
  
  // Configuración del modo de visualización
  const displayMode = process.env.REACT_APP_DATA_ENTRY_DISPLAY_MODE || 'accordion'; // 'accordion' | 'continuous'
  const isContinuousMode = displayMode === 'continuous';
  
  // Estado local para el banco seleccionado
  const [selectedBank, setSelectedBank] = useState(clientData?.bank || null);

  // Obtener configuración de secciones habilitadas
  const getEnabledSections = () => {
    const configString = process.env.REACT_APP_DATA_ENTRY_STEPS || 'PersonalInfo,AddressContact,ContactInfo,Referrals';
    return configString.split(',').map(section => section.trim());
  };

  const enabledSections = getEnabledSections();
  
  // Determinar si se debe mostrar cada sección específica
  const showPersonalInfo = enabledSections.includes('PersonalInfo');
  const showBankSection = enabledSections.includes('BankInformation');
  const showAddressContact = enabledSections.includes('AddressContact');
  const showContactInfo = enabledSections.includes('ContactInfo');
  const showReferrals = enabledSections.includes('Referrals');
  const showEmploymentSection = enabledSections.includes('EmploymentInfo') && clientData?.employment_status?.code === 'ACTIVE';

  const [formData, setFormData] = useState({
    firstName: clientData?.firstName || '',
    lastName: clientData?.lastName || '',
    birthDate: clientData?.birthDate || '',
    gender: clientData?.gender || '',
    ...(showBankSection && { cbu: '' }),
    referral: [
      { firstName: '', lastName: '', phone: '', relationship: '' },
      { firstName: '', lastName: '', phone: '', relationship: '' }
    ],
    ...initialData,
  });

  const [isLoading, setIsLoading] = useState(false);
  
  // Referencia para prevenir múltiples clics simultáneos
  const isProcessingRef = useRef(false);

  // Inicializar sección activa con la primera sección habilitada
  const getFirstEnabledSection = () => {
    const sectionsInOrder = ['PersonalInfo', 'BankInformation', 'AddressContact', 'ContactInfo', 'Referrals', 'EmploymentInfo'];
    return sectionsInOrder.find(section => {
      switch (section) {
        case 'PersonalInfo': return showPersonalInfo;
        case 'BankInformation': return showBankSection;
        case 'AddressContact': return showAddressContact;
        case 'ContactInfo': return showContactInfo;
        case 'Referrals': return showReferrals;
        case 'EmploymentInfo': return showEmploymentSection;
        default: return false;
      }
    }) || 'PersonalInfo';
  };

  const [activeSection, setActiveSection] = useState(isContinuousMode ? null : getFirstEnabledSection());
  
  // Configurar validez de secciones solo para las habilitadas
  const getInitialSectionValidity = () => {
    const validity = {};
    if (showPersonalInfo) validity.PersonalInfo = null;
    if (showBankSection) validity.BankInformation = null;
    if (showAddressContact) validity.AddressContact = null;
    if (showContactInfo) validity.ContactInfo = null;
    if (showReferrals) validity.Referrals = null;
    if (showEmploymentSection) validity.EmploymentInfo = null;
    return validity;
  };

  const [sectionValidity, setSectionValidity] = useState(getInitialSectionValidity());

  const [serverErrors, setServerErrors] = useState([]);

  const personalInfoRef = useRef();
  const bankInformationRef = useRef();
  const addressContactRef = useRef();
  const contactInfoRef = useRef();
  const referralsRef = useRef();
  const employmentInfoRef = useRef();

  // Obtener las secciones habilitadas para sugerencias
  const enabledSuggestedSections = process.env.REACT_APP_SUGGESTED_DATA_ENABLE?.split(',') || [];

  // Agregar estado para rastrear secciones ya cargadas
  const [loadedSections, setLoadedSections] = useState(new Set());
  const [loadingSections, setLoadingSections] = useState({
    personal: false,
    address: false,
    contact: false,
    referral: false,
    employment: false
  });

  // Efecto para cargar todas las sugerencias en modo continuo
  useEffect(() => {
    if (isContinuousMode) {
      // Cargar sugerencias de todas las secciones habilitadas
      const sectionsToLoad = ['PersonalInfo', 'AddressContact', 'ContactInfo', 'Referrals', 'EmploymentInfo'];
      sectionsToLoad.forEach(section => {
        const shouldLoad = () => {
          switch (section) {
            case 'PersonalInfo': return showPersonalInfo;
            case 'AddressContact': return showAddressContact;
            case 'ContactInfo': return showContactInfo;
            case 'Referrals': return showReferrals;
            case 'EmploymentInfo': return showEmploymentSection;
            default: return false;
          }
        };
        
        if (shouldLoad()) {
          loadSuggestedData(section);
        }
      });
    }
  }, [isContinuousMode, showPersonalInfo, showAddressContact, showContactInfo, showReferrals, showEmploymentSection]);

  // Efecto para validación automática en modo continuo
  useEffect(() => {
    if (isContinuousMode) {
      // Validar todas las secciones habilitadas automáticamente
      const validateAllSections = () => {
        const sectionsToValidate = [
          { name: 'PersonalInfo', ref: personalInfoRef, enabled: showPersonalInfo },
          { name: 'BankInformation', ref: bankInformationRef, enabled: showBankSection },
          { name: 'AddressContact', ref: addressContactRef, enabled: showAddressContact },
          { name: 'ContactInfo', ref: contactInfoRef, enabled: showContactInfo },
          { name: 'Referrals', ref: referralsRef, enabled: showReferrals },
          { name: 'EmploymentInfo', ref: employmentInfoRef, enabled: showEmploymentSection }
        ];

        sectionsToValidate.forEach(({ name, ref, enabled }) => {
          if (enabled && ref.current) {
            setTimeout(() => {
              ref.current.validateSection();
            }, 100);
          }
        });
      };

      // Validar después de que se carguen las sugerencias
      const timer = setTimeout(validateAllSections, 500);
      return () => clearTimeout(timer);
    }
  }, [isContinuousMode, selectedData, formData, showPersonalInfo, showBankSection, showAddressContact, showContactInfo, showReferrals, showEmploymentSection]);

  // Modificar la función loadSuggestedData
  const loadSuggestedData = async (section) => {
    const sectionToEndpoint = {
      'PersonalInfo': 'personal',
      'AddressContact': 'address', 
      'ContactInfo': 'contact',
      'Referrals': 'referral',
      'EmploymentInfo': 'employment'
    };

    const key = sectionToEndpoint[section];
    if (!key) return;

    // Verificar si la sección ya fue cargada
    if (loadedSections.has(key) || !enabledSuggestedSections.includes(key)) return;

    // Activar el estado de carga para la sección
    setLoadingSections(prev => ({
      ...prev,
      [key]: true
    }));

    try {
      const response = await api.get(`/customers/${clientData.id}/suggested-data/${key}`);
      if (response.data) {
        setSuggestedData(prev => ({
          ...prev,
          [key]: response.data.data
        }));
        // Marcar la sección como cargada
        setLoadedSections(prev => new Set([...prev, key]));
      }
    } catch (error) {
      console.error(`Error loading suggested ${section} data:`, error);
    } finally {
      // Desactivar el estado de carga para la sección
      setLoadingSections(prev => ({
        ...prev,
        [key]: false
      }));
    }
  };

  const handleChange = (e, options = {}) => {
    const { name, value } = e.target;
  
    let processedValue = value;
    
    // Procesamiento especial para ciertos campos
    if (name === 'phone' || name === 'workPhone') {
      processedValue = value.replace(/^0+/, ''); // Eliminar ceros a la izquierda para teléfono
    }
  
    setFormData((prev) => {
      if (options.isNested && options.arrayIndex !== undefined) {
        const updatedArray = [...prev[options.nestedKey]];
        updatedArray[options.arrayIndex][name] = processedValue;
        return {
          ...prev,
          [options.nestedKey]: updatedArray,
        };
      }
  
      return {
        ...prev,
        [name]: processedValue,
      };
    });
  };  

  const handleSectionClick = (targetSection) => {
    // En modo continuo, no manejar clics de sección
    if (isContinuousMode) {
      return;
    }

    // Verificar que la sección esté habilitada antes de permitir el clic
    const isSectionEnabled = () => {
      switch (targetSection) {
        case 'PersonalInfo': return showPersonalInfo;
        case 'BankInformation': return showBankSection;
        case 'AddressContact': return showAddressContact;
        case 'ContactInfo': return showContactInfo;
        case 'Referrals': return showReferrals;
        case 'EmploymentInfo': return showEmploymentSection;
        default: return false;
      }
    };

    if (!isSectionEnabled()) {
      console.warn(`Section ${targetSection} is not enabled`);
      return;
    }

    let currentSectionRef;

    switch (activeSection) {
      case 'PersonalInfo':
        currentSectionRef = showPersonalInfo ? personalInfoRef : null;
        break;
      case 'BankInformation':
        currentSectionRef = showBankSection ? bankInformationRef : null;
        break;
      case 'AddressContact':
        currentSectionRef = showAddressContact ? addressContactRef : null;
        break;
      case 'ContactInfo':
        currentSectionRef = showContactInfo ? contactInfoRef : null;
        break;
      case 'Referrals':
        currentSectionRef = showReferrals ? referralsRef : null;
        break;
      case 'EmploymentInfo':
        currentSectionRef = showEmploymentSection ? employmentInfoRef : null;
        break;
      default:
        currentSectionRef = null;
    }

    if (currentSectionRef && currentSectionRef.current) {
      currentSectionRef.current.validateSection();
    }

    if (activeSection === targetSection) {
      setActiveSection(null);
    } else {
      setActiveSection(targetSection);
      // Cargar sugerencias cuando se abre una nueva sección
      loadSuggestedData(targetSection);
    } 
  };

  const handleNext = async () => {
    // Prevenir múltiples clics simultáneos
    if (isProcessingRef.current) {
      console.warn('Proceso ya en curso, espere por favor...');
      return;
    }

    let currentSectionRef;
  
    switch (activeSection) {
      case 'PersonalInfo':
        currentSectionRef = showPersonalInfo ? personalInfoRef : null;
        break;
      case 'BankInformation':
        currentSectionRef = showBankSection ? bankInformationRef : null;
        break;
      case 'AddressContact':
        currentSectionRef = showAddressContact ? addressContactRef : null;
        break;
      case 'ContactInfo':
        currentSectionRef = showContactInfo ? contactInfoRef : null;
        break;
      case 'Referrals':
        currentSectionRef = showReferrals ? referralsRef : null;
        break;
      case 'EmploymentInfo':
        currentSectionRef = showEmploymentSection ? employmentInfoRef : null;
        break;
      default:
        currentSectionRef = null;
    }
  
    if (currentSectionRef && currentSectionRef.current && !currentSectionRef.current.validateSection()) {
      console.warn('No puede avanzar sin completar todos los campos requeridos.');
      return;
    }
  
    // Verificar que todas las secciones habilitadas sean válidas
    const enabledSectionsKeys = Object.keys(sectionValidity);
    const allEnabledSectionsValid = enabledSectionsKeys.every((sectionKey) => sectionValidity[sectionKey] === true);
    
    if (allEnabledSectionsValid) {
      // Marcar que el proceso está en curso
      isProcessingRef.current = true;
      setIsLoading(true);
      
      try {
        const success = await handleSubmit();
        if (success) {
          onNext({ 
            personalInfo: formData,
            contactPhone: formData.phone // Pasar el teléfono para uso en pasos posteriores
          });
        } else {
          console.error('No se pudo guardar la información. Por favor, revise los errores.');
        }
      } catch (error) {
        console.error('Error en handleNext:', error);
      } finally {
        // Limpiar el estado de procesamiento
        isProcessingRef.current = false;
        setIsLoading(false);
      }
    } else {
      console.warn('Por favor, complete todas las secciones antes de continuar.');
    }
  };  

  const handleBankChange = (newBankData) => {
    setSelectedBank(newBankData);
  };

  const handleSubmit = async () => {
    setServerErrors([]);
    
    const payload = {
      purchase_request_id: purchaseRequestId,
      enabled_sections: enabledSections,
      validation_rules: {
        referrals: {
          min_count: parseInt(process.env.REACT_APP_MIN_REFERRALS || '1')
        }
      },
      existingData: {
        address: (selectedData.address?.toString().startsWith('new_') || selectedData.address?.toString().startsWith('edited_')) ? null : selectedData.address,
        referral: selectedData.referral?.filter(id => !id.toString().startsWith('new_') && !id.toString().startsWith('edited_')) || [],
        contact: (selectedData.contact?.toString().startsWith('new_') || selectedData.contact?.toString().startsWith('edited_') || selectedData.contact?.toString().startsWith('contact_')) ? null : selectedData.contact,
        employment: (selectedData.employment?.toString().startsWith('new_') || selectedData.employment?.toString().startsWith('edited_')) ? null : selectedData.employment
      },
      personalInfo: {
        firstname: formData.firstName,
        lastname: formData.lastName,
        gender: formData.gender,
        birthdate: formData.birthDate,
      },
      ...(showBankSection && selectedBank && {
        bankInfo: {
          bank_id: selectedBank?.id,
          cbu: formData.cbu
        }
      }),
      address: (selectedData.address?.toString().startsWith('new_') || selectedData.address?.toString().startsWith('edited_')) ? {
        address: formData.address,
        provinceId: formData.province,
        localityId: formData.locality,
        postalCode: formData.postalCode ? `${formData.postalCode}` : '',
        parent_id: (() => {
          const selectedAddress = suggestedData.address?.find(addr => addr.id === selectedData.address);
          return selectedAddress?.parent_id || null;
        })(),
      } : null,
      contact: (selectedData.contact?.toString().startsWith('new_') || selectedData.contact?.toString().startsWith('edited_') || selectedData.contact?.toString().startsWith('contact_')) ? {
        mobile: formData.phone,
        email: formData.email,
      } : null,
      referrals: suggestedData.referral
        ?.filter(referral => 
          selectedData.referral?.includes(referral.id) && 
          (referral.id.toString().startsWith('new_') || referral.id.toString().startsWith('edited_'))
        )
        .map(referral => ({
          firstname: referral.firstName,
          lastname: referral.lastName,
          phone: referral.phone,
          relationship: referral.relationship,
        })) || [],
      // Si hay una sugerencia de empleo seleccionada, enviamos null
      ...(showEmploymentSection && {
        employmentInfo: (selectedData.employment?.toString().startsWith('new_') || selectedData.employment?.toString().startsWith('edited_')) ? {
          companyName: formData.companyName,
          entryDate: formData.entryDate,
          position: formData.position,
          workPhone: formData.workPhone,
          workAddress: formData.workAddress,
          workProvinceId: formData.workProvince,
          workLocalityId: formData.workLocality,
          workPostalCode: `${formData.workPostalCode}`,
        } : null
      }),
    };

    console.log('selectedData: ', selectedData); 
    console.log('formData: ', formData); 
    console.log('payload: ', payload);

    try {
      const response = await api.post(`/customers/${clientData.id}/data`, payload);
      if (response.status === 200) {
        console.log('Customer data saved successfully');
        return true; 
      }
    } catch (error) {
      console.error('Error updating customer data:', error);
      
      if (error.response?.data?.errors) {
        const errorMessages = Object.values(error.response.data.errors)
          .flat()
          .map(msg => msg.replace(/employment info\./g, ''));
        
        setServerErrors(errorMessages);
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Hubo un problema al guardar los datos. Por favor, intente nuevamente.',
        });
      }
      return false;
    }    
  };  

  return (
    <div className="container my-5">
      <h4 className="text-center mb-2">Complete sus datos personales</h4>
      <p className="text-center mb-4 text-muted">
        Estos datos serán utilizados para el legajo digital y la solicitud de préstamo. 
      </p>

      {/* Contenedor de formularios - modo accordion o continuo */}
      <div className={isContinuousMode ? "continuous-form-container" : "accordion"} id={isContinuousMode ? "datosContinuous" : "datosAccordion"}>
        {showPersonalInfo && (
          <PersonalInfo
            ref={personalInfoRef}
            formData={formData}
            handleChange={handleChange}
            activeSection={isContinuousMode ? "PersonalInfo" : activeSection}
            sectionName="PersonalInfo"
            sectionValidity={sectionValidity}
            setSectionValidity={setSectionValidity}
            handleSectionClick={handleSectionClick}
            isLoadingSuggestions={loadingSections.personal}
            displayMode={displayMode}
          />
        )}
        {showBankSection && (
          <BankInformation
            ref={bankInformationRef}
            formData={formData}
            handleChange={handleChange}
            activeSection={isContinuousMode ? "BankInformation" : activeSection}
            sectionName="BankInformation"
            sectionValidity={sectionValidity}
            setSectionValidity={setSectionValidity}
            handleSectionClick={handleSectionClick}
            bankData={selectedBank}
            onBankChange={handleBankChange}
            clientData={clientData}
            displayMode={displayMode}
          />
        )}
        {showAddressContact && (
          <AddressContact
            ref={addressContactRef}
            formData={formData}
            handleChange={handleChange}
            activeSection={isContinuousMode ? "AddressContact" : activeSection}
            sectionName="AddressContact"
            sectionValidity={sectionValidity}
            setSectionValidity={setSectionValidity}
            handleSectionClick={handleSectionClick}
            isLoadingSuggestions={loadingSections.address}
            displayMode={displayMode}
            clientData={clientData}
          />
        )}
        {showContactInfo && (
          <ContactInfo
            ref={contactInfoRef}
            formData={formData}
            handleChange={handleChange}
            activeSection={isContinuousMode ? "ContactInfo" : activeSection}
            sectionName="ContactInfo"
            sectionValidity={sectionValidity}
            setSectionValidity={setSectionValidity}
            handleSectionClick={handleSectionClick}
            isLoadingSuggestions={loadingSections.contact}
            displayMode={displayMode}
            clientData={clientData}
          />
        )}
        {showEmploymentSection && (
          <EmploymentInfo
            ref={employmentInfoRef}
            formData={formData}
            handleChange={handleChange}
            activeSection={isContinuousMode ? "EmploymentInfo" : activeSection}
            sectionName="EmploymentInfo"
            sectionValidity={sectionValidity}
            setSectionValidity={setSectionValidity}
            handleSectionClick={handleSectionClick}
            isLoadingSuggestions={loadingSections.employment}
            displayMode={displayMode}
            clientData={clientData}
          />
        )}
        {showReferrals && (
          <Referrals
            ref={referralsRef}
            formData={formData}
            handleChange={handleChange}
            activeSection={isContinuousMode ? "Referrals" : activeSection}
            sectionName="Referrals"
            sectionValidity={sectionValidity}
            setSectionValidity={setSectionValidity}
            handleSectionClick={handleSectionClick}
            isLoadingSuggestions={loadingSections.referral}
            displayMode={displayMode}
          />
        )}
      </div>

      {serverErrors.length > 0 && (
        <div className="alert alert-warning mt-3" role="alert">
          <strong className="alert-heading"><i class="bi bi-exclamation-triangle me-2"></i>Por favor, corrija los siguientes errores:</strong>
          <ul className="mb-0 ms-3 list-unstyled">
            {serverErrors.map((error, index) => (
              <li key={index}>• {error}</li>
            ))}
          </ul>
        </div>
      )}

      <NavigationButtons
        onBack={onBack}
        onNext={handleNext}
        disableNext={!Object.keys(sectionValidity).every((sectionKey) => sectionValidity[sectionKey] === true)}
        isLoading={isLoading}
      />
    </div>
  );
};

export default DataEntryStep;
